/**
 * feedQueries.js — PRODUCTION v4
 * Place at: src/lib/feedQueries.js
 *
 * CHANGE vs v3:
 *   fetchComments now fetches votes only for loaded comment IDs
 *   instead of fetching ALL comment votes ever made by the user globally.
 *   This prevents a massive query for active users with thousands of votes.
 */

import { supabase } from "../supabase";

export const FEED_PAGE_SIZE    = 10;
export const COMMENT_PAGE_SIZE = 20;

// ─── ABLY DELIVERY ─────────────────────────────────────────────────────────────
export function publishToAbly(channel, data) {
  fetch("https://healhub-auth.apihealhubspace.workers.dev/publish", {
    method:  "POST",
    headers: { "Content-Type": "application/json" },
    body:    JSON.stringify({ channel, data }),
  }).catch(err => {
    if (process.env.NODE_ENV === "development") {
      console.warn("[Ably] publish failed (non-fatal):", channel, err.message);
    }
  });
}

// ─── FEED QUERIES ──────────────────────────────────────────────────────────────

export async function fetchFeedPage(
  userId,
  { page = 0, tag = null, search = null, sort = "hot", pageSize = FEED_PAGE_SIZE, signal, authorId = null } = {}
) {
  const from = page * pageSize;
  const to   = from + pageSize - 1;

  const { data, error } = await supabase.rpc("get_feed_with_user_state", {
    p_user_id:   userId,
    p_from:      from,
    p_to:        to,
    p_tag:       tag    || null,
    p_search:    search || null,
    p_sort:      sort,
    p_author_id: authorId || null,
  }, { signal });

  if (error) throw error;

  const posts = (data || []).filter(p => !p.is_deleted && !p.is_hidden);
  const voteMap = {}, bookmarkMap = {}, reportMap = {};
  posts.forEach(p => {
    if (p.my_vote)       voteMap[p.id]     = p.my_vote;
    if (p.is_bookmarked) bookmarkMap[p.id] = true;
    if (p.is_reported)   reportMap[p.id]   = true;
  });
  return { posts, voteMap, bookmarkMap, reportMap };
}

export async function fetchFollowingPosts(
  userId,
  { page = 0, pageSize = FEED_PAGE_SIZE, signal } = {}
) {
  const from = page * pageSize;
  const to   = from + pageSize - 1;

  const { data, error } = await supabase.rpc("get_following_posts", {
    p_user_id: userId,
    p_from:    from,
    p_to:      to,
  }, { signal });
  if (error) throw error;

  const posts = (data || []).filter(p => !p.is_deleted && !p.is_hidden);
  if (!posts.length) return { posts: [], voteMap: {}, bookmarkMap: {}, reportMap: {} };

  const voteMap = {}, bookmarkMap = {}, reportMap = {};
  posts.forEach(p => {
    if (p.my_vote)       voteMap[p.id]     = p.my_vote;
    if (p.is_bookmarked) bookmarkMap[p.id] = true;
    if (p.is_reported)   reportMap[p.id]   = true;
  });
  return { posts, voteMap, bookmarkMap, reportMap };
}

export async function fetchBookmarkedPosts(
  userId,
  { page = 0, pageSize = FEED_PAGE_SIZE, signal } = {}
) {
  const from = page * pageSize;
  const to   = from + pageSize - 1;

  const { data, error } = await supabase.rpc("get_bookmarked_posts", {
    p_user_id: userId,
    p_from:    from,
    p_to:      to,
  }, { signal });
  if (error) throw error;

  const posts = (data || []).filter(p => p && !p.is_deleted && !p.is_hidden);
  if (!posts.length) return { posts: [], voteMap: {}, bookmarkMap: {}, reportMap: {} };

  const voteMap = {}, bookmarkMap = {}, reportMap = {};
  posts.forEach(p => {
    bookmarkMap[p.id] = true;
    if (p.my_vote)     voteMap[p.id]    = p.my_vote;
    if (p.is_reported) reportMap[p.id]  = true;
  });
  return { posts, voteMap, bookmarkMap, reportMap };
}

// ─── VOTE ─────────────────────────────────────────────────────────────────────

export async function atomicVote(postId, userId, voteType, previousVote) {
  const { error } = await supabase.rpc("atomic_vote", {
    p_post_id:       postId,
    p_user_id:       userId,
    p_vote_type:     voteType,
    p_previous_vote: previousVote,
  });
  return { error };
}

// ─── COMMENTS ─────────────────────────────────────────────────────────────────

export async function fetchComments(
  postId,
  userId,
  { cursor = null, pageSize = COMMENT_PAGE_SIZE } = {}
) {
  let query = supabase
    .from("comments")
    .select("id, post_id, author_id, author_username, content, upvotes, downvotes, is_deleted, created_at")
    .eq("post_id", postId)
    .eq("is_deleted", false)
    .order("created_at", { ascending: true })
    .limit(pageSize);

  if (cursor) query = query.gt("created_at", cursor);

  // Fetch comments first
  const commentsRes = await query;
  if (commentsRes.error) throw commentsRes.error;

  const comments   = commentsRes.data || [];
  const hasMore    = comments.length === pageSize;
  const nextCursor = hasMore ? comments[comments.length - 1].created_at : null;

  // Fetch votes only for loaded comment IDs — not all votes globally
  const commentVoteMap = {};
  if (userId && comments.length > 0) {
    const { data: votes } = await supabase
      .from("comment_votes")
      .select("comment_id, vote_type")
      .eq("user_id", userId)
      .in("comment_id", comments.map(c => c.id));
    (votes || []).forEach(v => { commentVoteMap[v.comment_id] = v.vote_type; });
  }

  return { comments, commentVoteMap, hasMore, nextCursor };
}

export async function submitComment({ postId, authorId, authorUsername, content }) {
  const { data, error } = await supabase
    .rpc("submit_comment", {
      p_post_id:         postId,
      p_author_id:       authorId,
      p_author_username: authorUsername,
      p_content:         content.trim(),
    })
    .single();
  if (error) throw error;

  publishToAbly(`comments:${postId}`, data);
  return data;
}

// ─── NOTIFICATIONS ────────────────────────────────────────────────────────────

export async function fetchNotifications(userId) {
  const { data, error } = await supabase
    .from("notifications")
    .select("id, type, actor_username, post_id, message, is_read, created_at")
    .eq("recipient_id", userId)
    .order("created_at", { ascending: false })
    .limit(20);
  if (error) throw error;
  return data || [];
}

export async function upsertNotification({ recipientId, type, actorUsername, postId, message }) {
  const { data, error } = await supabase
    .from("notifications")
    .upsert(
      { recipient_id: recipientId, type, actor_username: actorUsername, post_id: postId, message, is_read: false },
      { onConflict: "recipient_id,type,actor_username,post_id", ignoreDuplicates: false }
    )
    .select()
    .single();
  if (error) throw error;
  return data;
}

// ─── USER ─────────────────────────────────────────────────────────────────────

export async function incrementPostCount(userId) {
  const { error } = await supabase.rpc("increment_post_count", { p_user_id: userId });
  if (error) throw error;
}

export async function handleFollow(followerId, followingId, action) {
  const { error } = await supabase.rpc("handle_follow", {
    p_follower_id:  followerId,
    p_following_id: followingId,
    p_action:       action,
  });
  if (error) throw error;
}

// ─── SPACES ───────────────────────────────────────────────────────────────────

export async function fetchUserSpaces(userId) {
  const { data, error } = await supabase
    .from("space_members")
    .select("spaces(id, name, display_name, avatar_emoji, color)")
    .eq("user_id", userId)
    .neq("role", "banned");
  if (error) throw error;
  return (data || []).map(m => m.spaces).filter(Boolean);
}

export async function checkNewSpacePosts(spaceId, since) {
  const { data, error } = await supabase
    .from("posts")
    .select("count")
    .eq("space_id", spaceId)
    .gt("created_at", since)
    .eq("is_deleted", false);
  if (error) throw error;
  return data?.[0]?.count ?? 0;
}